import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  MONITOR_PERMISSIONS,
  AGENDA_PERMISSIONS,
  PRODUCT_ORDERS_PERMISSIONS,
  AGENT_PERMISSIONS,
  TEAM_PERMISSIONS,
  INBOX_PERMISSIONS,
  ASSIGNMENT_POLICY_PERMISSIONS,
  AUTOMATION_PERMISSIONS,
  LABEL_PERMISSIONS,
  CUSTOM_ATTRIBUTE_PERMISSIONS,
  INTEGRATION_PERMISSIONS,
  CAMPAIGN_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const CAPTAIN_FEATURE_FLAGS = [
  FEATURE_FLAGS.CAPTAIN,
  FEATURE_FLAGS.CAPTAIN_V2,
  FEATURE_FLAGS.CAPTAIN_TASKS,
];

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const routeFeatureIsEnabledFor = (route, account = {}) => {
  const {
    meta: { featureFlag = null } = {},
  } = route;

  if (!featureFlag) return true;

  const { features = {} } = account;

  if (CAPTAIN_FEATURE_FLAGS.includes(featureFlag)) {
    return CAPTAIN_FEATURE_FLAGS.some(flag => features[flag] === true);
  }

  return features[featureFlag] || false;
};

export const getRouteBlockReason = (route, user) => {
  const currentAccount = getCurrentAccount(user, Number(route.params.accountId));
  if (!currentAccount || currentAccount.status !== 'active') {
    return null;
  }

  const userPermissions = getUserPermissions(user, route.params.accountId);

  if (!routeIsAccessibleFor(route, userPermissions)) {
    return 'permission_denied';
  }

  if (!routeFeatureIsEnabledFor(route, currentAccount)) {
    return 'feature_disabled';
  }

  return null;
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [MONITOR_PERMISSIONS], path: 'monitor' },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [AGENDA_PERMISSIONS], path: 'agenda' },
    {
      permissions: [PRODUCT_ORDERS_PERMISSIONS],
      path: 'products-orders',
    },
    { permissions: [CAMPAIGN_PERMISSIONS], path: 'campaigns/live_chat' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
    { permissions: [AGENT_PERMISSIONS], path: 'settings/agents/list' },
    { permissions: [TEAM_PERMISSIONS], path: 'settings/teams/list' },
    {
      permissions: [ASSIGNMENT_POLICY_PERMISSIONS],
      path: 'settings/assignment-policy/index',
    },
    { permissions: [INBOX_PERMISSIONS], path: 'settings/inboxes/list' },
    { permissions: [LABEL_PERMISSIONS], path: 'settings/labels/list' },
    {
      permissions: [CUSTOM_ATTRIBUTE_PERMISSIONS],
      path: 'settings/custom-attributes/list',
    },
    { permissions: [AUTOMATION_PERMISSIONS], path: 'settings/automation/list' },
    {
      permissions: [INTEGRATION_PERMISSIONS],
      path: 'settings/integrations',
    },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  const isFeatureEnabled = routeFeatureIsEnabledFor(to, currentAccount);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible && isFeatureEnabled
    ? null
    : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, only the suspended screen is
  // reachable; administrators can also access billing to restore the account
  const userPermissions = getUserPermissions(user, to.params.accountId);
  const accessibleRoutes = userPermissions.includes('administrator')
    ? ['account_suspended', 'billing_settings_index']
    : ['account_suspended'];

  if (!accessibleRoutes.includes(to.name)) {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
    'monitor_dashboard',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
    'monitor_conversation',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    case 'monitor_conversation':
      return 'monitor_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isUpgradePageBypassRoute = routeName =>
  [
    'billing_settings_index',
    'settings_inbox_list',
    'general_settings_index',
    'agent_list',
  ].includes(routeName);
